/**
 * Product store — product data, colors, attributes, pricing, views.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { eventBus } from '@core/EventBus';
import { useConfigStore } from './configStore';
import type { PrintingTypeConfig } from './configStore';
import { normalizeColorParam, replaceDesignerUrlState } from '../utils/urlState';
import type {
  AttributeSelections,
  AttributeSelectionValue,
  NormalizedProductAttribute,
} from '@shared/productAttributes';

export interface ProductColor {
  hex: string;       // may contain semicolons for multi-color (e.g. "FF0000;0000FF")
  title: string;
  /** Optional texture image for color swatch / mockup (URL). */
  textureUrl?: string;
  /** SVG data per view for this color variant */
  views: Record<string, string>;
}

export type ProductAttribute = NormalizedProductAttribute;

export interface ProductData {
  id: string;
  title: string;
  description: string;
  shortDescription: string;
  sku: string;
  image: string;
  price: number;
  regularPrice: number;
  salePrice: number;
  colors: ProductColor[];
  attributes: ProductAttribute[];
  /** Design area boundaries per view (JSON string from server) */
  areas: Record<string, string>;
  /** SVG params per view */
  params: Record<string, string>;
}

export interface WcProductInfo {
  id: number;
  title: string;
  description: string;
  shortDescription: string;
  image: string;
  gallery: string[];
  sku: string;
  regularPrice: number;
  salePrice: number;
  stockStatus: string;
}

export interface LinkedProduct {
  id: number;
  title: string;
  slug: string;
  image: string;
  price: number;
}

export interface PriceBreakdownLine {
  key?: string;
  label: string;
  unit?: number;
  qty?: number;
  total?: number;
  /** 'line' = one-off per order line (e.g. screen setup); default per unit. */
  per?: 'unit' | 'line';
  /** Per-side share of `total` ({view: amount}); absent for shared fees. */
  views?: Record<string, number>;
}

export interface PriceBreakdown {
  product: number;
  regular: number;
  color: number;
  clipart: number;
  printing: { code: string; title: string; amount: number };
  personalization: { fee: number; lines: PriceBreakdownLine[] };
  attribute: number;
  unit_price: number;
  line_total: number;
  quantity: number;
  components?: PriceBreakdownLine[];
  version?: number;
}

export type PriceStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export const useProductStore = defineStore('product', () => {
  const product = ref<ProductData | null>(null);
  const activeColorIndex = ref(0);
  const selectedAttributes = ref<AttributeSelections>({});
  const attributeErrorsVisible = ref(false);
  const quantity = ref(1);
  const price = ref(0);
  const oldPrice = ref(0);
  // Tax-display metadata mirrored from the server (WooCommerce "Display prices" setting).
  const priceSuffix = ref('');
  const priceIncludesTax = ref(false);
  const breakdown = ref<PriceBreakdown | null>(null);
  const priceStatus = ref<PriceStatus>('idle');
  // Why the price is unavailable, or what blocks ordering it (e.g. a minimum quantity), from the server.
  const priceMessage = ref('');
  const wcProduct = ref<WcProductInfo | null>(null);
  const linkedProducts = ref<LinkedProduct[]>([]);
  const availablePrintings = ref<PrintingTypeConfig[]>([]);
  const selectedPrintingCode = ref('');

  /** Ad-hoc custom color (not in catalog) chosen via the custom color picker. */
  const customTintHex = ref<string | null>(null);

  const activeColor = computed(() =>
    product.value?.colors[activeColorIndex.value] ?? null,
  );

  /** Effective tint hex: custom color when set, otherwise the active catalog color. */
  const tintHex = computed(
    () => customTintHex.value ?? activeColor.value?.hex ?? '000000',
  );

  const colorHexList = computed(() => {
    if (customTintHex.value) return customTintHex.value.split(';');
    const c = activeColor.value;
    if (!c) return [];
    return c.hex.split(';');
  });

  /**
   * Total of "Quantity per option" fields (e.g. S × 2, M × 3). When set it is
   * the order quantity, as on the product page and in the cart.
   */
  const sizeQuantity = computed(() =>
    Object.values(selectedAttributes.value).reduce((total, value) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return total;
      return total + Object.values(value).reduce((sum, qty) => sum + Math.max(0, Math.floor(Number(qty) || 0)), 0);
    }, 0),
  );

  /**
   * Per-unit extra each side adds ("+$5.00" under Back), from the server's per-side
   * attribution. One-off per-line fees are left out so the hint matches the unit price.
   */
  const viewExtras = computed<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const line of breakdown.value?.personalization.lines ?? []) {
      if (line.per === 'line' || !line.views) continue;
      for (const [view, amount] of Object.entries(line.views)) {
        const n = Number(amount);
        if (Number.isFinite(n) && n > 0) out[view] = (out[view] ?? 0) + n;
      }
    }
    return out;
  });

  const selectedPrinting = computed(() =>
    availablePrintings.value.find((printing) => printing.printing_code === selectedPrintingCode.value) ?? null,
  );

  function syncUrlState() {
    const color = normalizeColorParam(customTintHex.value ?? activeColor.value?.hex);
    replaceDesignerUrlState({
      color,
      qty: quantity.value,
    });
  }

  function setProduct(data: ProductData) {
    product.value = data;
    activeColorIndex.value = 0;
    customTintHex.value = null;
    selectedAttributes.value = {};
    attributeErrorsVisible.value = false;
    // Keep display refs in sync (UI reads price/oldPrice, not product.value.price).
    const sale = Number(data.salePrice) || 0;
    const reg = Number(data.regularPrice) || 0;
    const base = Number(data.price) || 0;
    const display = sale > 0 ? sale : base > 0 ? base : reg;
    if (display > 0) {
      const old = sale > 0 && reg > sale ? reg : 0;
      price.value = display;
      oldPrice.value = old;
    }
  }

  function changeColor(index: number) {
    if (!product.value || index < 0 || index >= product.value.colors.length) return;
    customTintHex.value = null; // picking a catalog swatch clears any custom color
    activeColorIndex.value = index;
    syncUrlState();
    eventBus.emit('change.color.design', { color: product.value.colors[index].hex });
  }

  /** Set an ad-hoc custom tint color (e.g. from a native color input). */
  function setCustomColor(hex: string) {
    const normalized = (hex || '').replace(/^#/, '').trim();
    if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return;
    customTintHex.value = normalized;
    syncUrlState();
    eventBus.emit('change.color.design', { color: normalized });
  }

  function clearCustomColor() {
    customTintHex.value = null;
    syncUrlState();
  }

  function setAttribute(slug: string, value: AttributeSelectionValue) {
    selectedAttributes.value[slug] = value;
    if (sizeQuantity.value > 0) setQuantity(sizeQuantity.value);
  }

  function setAttributes(values: AttributeSelections) {
    selectedAttributes.value = { ...values };
    if (sizeQuantity.value > 0) setQuantity(sizeQuantity.value);
  }

  function showAttributeErrors() {
    attributeErrorsVisible.value = true;
  }

  function setQuantity(qty: number) {
    const cfg = useConfigStore();
    const min = cfg.data.minOrder > 0 ? cfg.data.minOrder : 1;
    const max = cfg.data.maxOrder >= min ? cfg.data.maxOrder : min;
    const wanted = sizeQuantity.value > 0 ? sizeQuantity.value : qty;
    quantity.value = Math.max(min, Math.min(max, Math.round(wanted)));
    syncUrlState();
  }

  function setPrice(newPrice: number, newOldPrice?: number) {
    const basePayload = {
      price: newPrice,
      oldPrice: newOldPrice,
      quantity: quantity.value,
      product: product.value,
      wcProduct: wcProduct.value,
    };
    const filteredPrice = typeof window !== 'undefined'
      ? window.Veyop?.hooks?.applyFilters('veyo_frontend_price', newPrice, basePayload) ?? newPrice
      : newPrice;

    price.value = Number(filteredPrice) || 0;
    if (newOldPrice !== undefined) oldPrice.value = newOldPrice;

    const payload = {
      ...basePayload,
      price: price.value,
      oldPrice: oldPrice.value,
    };
    eventBus.emit('update.price.design', { price: String(price.value) });
    if (typeof window !== 'undefined') {
      window.Veyop?.hooks?.doAction('veyo_price_updated', payload);
    }
  }

  function setPriceMeta(meta: { suffix?: string; includesTax?: boolean; breakdown?: PriceBreakdown | null }) {
    if (meta.suffix !== undefined) priceSuffix.value = meta.suffix;
    if (meta.includesTax !== undefined) priceIncludesTax.value = meta.includesTax;
    if (meta.breakdown !== undefined) breakdown.value = meta.breakdown;
  }

  function setPriceStatus(status: PriceStatus, message = '') {
    priceStatus.value = status;
    priceMessage.value = message;
  }

  function setWcProduct(data: WcProductInfo) {
    wcProduct.value = data;
  }

  function setLinkedProducts(products: LinkedProduct[]) {
    linkedProducts.value = products;
  }

  function setAvailablePrintings(printings: PrintingTypeConfig[]) {
    availablePrintings.value = Array.isArray(printings) ? printings : [];
    if (!availablePrintings.value.length) {
      selectedPrintingCode.value = '';
      return;
    }
    if (!selectedPrintingCode.value || !availablePrintings.value.some((printing) => printing.printing_code === selectedPrintingCode.value)) {
      selectedPrintingCode.value = availablePrintings.value[0].printing_code;
    }
  }

  function setSelectedPrintingCode(code: string) {
    selectedPrintingCode.value = availablePrintings.value.some((printing) => printing.printing_code === code)
      ? code
      : '';
  }

  return {
    product,
    activeColorIndex,
    selectedAttributes,
    attributeErrorsVisible,
    quantity,
    sizeQuantity,
    price,
    oldPrice,
    priceSuffix,
    priceIncludesTax,
    breakdown,
    priceStatus,
    priceMessage,
    viewExtras,
    activeColor,
    customTintHex,
    tintHex,
    colorHexList,
    wcProduct,
    linkedProducts,
    availablePrintings,
    selectedPrintingCode,
    selectedPrinting,
    setProduct,
    changeColor,
    setCustomColor,
    clearCustomColor,
    setAttribute,
    setAttributes,
    showAttributeErrors,
    setQuantity,
    setPrice,
    setPriceMeta,
    setPriceStatus,
    setWcProduct,
    setLinkedProducts,
    setAvailablePrintings,
    setSelectedPrintingCode,
  };
});
